import React, { useState, useEffect } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import axios from 'axios'

// Create a separate axios instance WITHOUT interceptors for notifications
const notificationAxios = axios.create({
  baseURL: '/api',
  timeout: 5000,
})

const ClientAdminLayout = ({ children }) => {
  console.log('🏗️ ClientAdminLayout component rendering')

  const { tenant } = useTenant()
  const navigate = useNavigate()
  const location = useLocation()
  const [user, setUser] = useState(null)
  const [showLoader, setShowLoader] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [notificationCount, setNotificationCount] = useState(0)

  console.log('📊 notificationCount state:', notificationCount)

  // Fetch pending edit request count for badge
  useEffect(() => {
    console.log('⚡ useEffect for badge triggered!')

    // Fetch once on mount
    const timer = setTimeout(() => {
      console.log('⏰ Timeout completed, calling fetchPendingEditRequests')
      fetchPendingEditRequests()
    }, 1000) // Wait 1 second after login

    // Poll every 60 seconds for new edit requests
    const interval = setInterval(fetchPendingEditRequests, 60000)

    return () => {
      clearTimeout(timer)
      clearInterval(interval)
    }
  }, [])

  const fetchPendingEditRequests = async () => {
    console.log('🔔 Fetching pending edit requests...')
    try {
      const token = localStorage.getItem('tenant_admin_token')
      console.log('Token exists:', !!token)

      if (!token) {
        console.log('No token found, returning')
        return
      }

      // Use separate axios instance to avoid logout interceptor
      const response = await notificationAxios.get('/client-admin/member-edit-requests/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      }).catch((err) => {
        console.error('Axios error:', err)
        return null
      })

      console.log('Full response:', response)
      console.log('Response data:', response?.data)

      if (response?.data?.stats?.pending !== undefined) {
        console.log('✅ Setting pending count:', response.data.stats.pending)
        setNotificationCount(response.data.stats.pending)
      } else {
        console.log('❌ No pending count in response')
      }
    } catch (error) {
      console.error('❌ Error fetching edit requests:', error)
    }
  }

  const menuItems = [
    {
      path: '/admin/dashboard',
      icon: 'fas fa-tachometer-alt',
      label: 'Dashboard',
      badge: null
    },
    {
      path: '/admin/users',
      icon: 'fas fa-users-cog',
      label: 'Users',
      badge: null
    },
    {
      path: '/admin/players',
      icon: 'fas fa-users',
      label: 'Players',
      badge: null
    },
    {
      path: '/admin/officials',
      icon: 'fas fa-user-tie',
      label: 'Officials',
      badge: null
    },
    {
      path: '/admin/events',
      icon: 'fas fa-calendar-alt',
      label: 'Tournaments',
      badge: null
    },
    {
      path: '/admin/registration-requests',
      icon: 'fas fa-clipboard-check',
      label: 'Registration Requests',
      badge: null
    },
    {
      path: '/admin/notifications',
      icon: 'fas fa-bell',
      label: 'Notifications',
      badge: notificationCount > 0 ? notificationCount : null // Only show when count > 0
    },
    {
      path: '/admin/certificates',
      icon: 'fas fa-certificate',
      label: 'Certificates',
      badge: null
    },
    {
      path: '/admin/state-tournament-records',
      icon: 'fas fa-trophy',
      label: 'State Tournament',
      badge: null
    },
    {
      path: '/admin/settings',
      icon: 'fas fa-cog',
      label: 'Settings',
      badge: null
    }
  ]

  useEffect(() => {
    const storedUser = localStorage.getItem('tenant_admin_user')
    if (storedUser) {
      setUser(JSON.parse(storedUser))
    }
  }, [])

  // Show simple loader on route change
  useEffect(() => {
    setShowLoader(true)

    const hideTimer = setTimeout(() => {
      setShowLoader(false)
    }, 300) // Quick 300ms loader

    return () => {
      clearTimeout(hideTimer)
    }
  }, [location.pathname])

  const handleLogout = () => {
    localStorage.removeItem('tenant_admin_token')
    localStorage.removeItem('tenant_admin_user')
    navigate('/user-login')
  }

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  return (
    <div className="admin-layout">
      {/* Simple Loader */}
      {showLoader && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          height: '3px',
          background: 'linear-gradient(90deg, #022a5e, #0ea5e9, #022a5e)',
          backgroundSize: '200% 100%',
          animation: 'loadingBar 1s ease-in-out infinite'
        }}>
          <style>
            {`
              @keyframes loadingBar {
                0% { background-position: 200% 0; }
                100% { background-position: -200% 0; }
              }
            `}
          </style>
        </div>
      )}

      {/* Sidebar */}
      <aside
        className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}
        style={{ background: '#ffffff', borderRight: '1px solid #e5e7eb' }}
      >
        {/* Logo/Brand */}
        <div className="sidebar-header" style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
          <div className="brand">
            <i className="fas fa-baseball-ball" style={{ color: '#b91c1c' }}></i>
            {!sidebarCollapsed && <span className="brand-text" style={{ color: '#b91c1c' }}>{tenant?.name || 'Admin'}</span>}
          </div>
          <button
            className="toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
          >
            <i className={`fas fa-${sidebarCollapsed ? 'angle-right' : 'angle-left'}`}></i>
          </button>
        </div>

        {/* Menu Items */}
        <nav className="sidebar-nav" style={{ background: '#ffffff' }}>
          <ul className="menu-list">
            {menuItems.map((item) => (
              <li key={item.path} className={isActive(item.path) ? 'active' : ''}>
                <Link
                  to={item.path}
                  title={sidebarCollapsed ? item.label : ''}
                  style={{
                    background: isActive(item.path) ? '#fef2f2' : 'transparent',
                    color: isActive(item.path) ? '#b91c1c' : '#4b5563',
                    borderLeft: isActive(item.path) ? '3px solid #b91c1c' : 'none',
                    position: 'relative'
                  }}
                >
                  <i className={item.icon}></i>
                  {!sidebarCollapsed && (
                    <>
                      <span className="menu-label">{item.label}</span>
                      {(item.badge !== null && item.badge !== undefined) && (
                        <span className="menu-badge" style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: '#dc2626',
                          color: 'white',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          minWidth: '18px',
                          textAlign: 'center'
                        }}>
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {sidebarCollapsed && (item.badge !== null && item.badge !== undefined) && (
                    <span className="menu-badge-dot" style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '8px',
                      height: '8px',
                      background: '#dc2626',
                      borderRadius: '50%',
                      border: '2px solid white'
                    }}></span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Info & Logout */}
        <div className="sidebar-footer" style={{ background: '#ffffff', borderTop: '1px solid #e5e7eb' }}>
          {!sidebarCollapsed && user && (
            <div className="user-info" style={{ background: '#f9fafb', border: '1px solid #e5e7eb' }}>
              <i className="fas fa-user-circle" style={{ color: '#b91c1c' }}></i>
              <span className="user-name" style={{ color: '#374151' }}>{user.name}</span>
            </div>
          )}
          <button
            className="logout-btn"
            onClick={handleLogout}
            title={sidebarCollapsed ? 'Logout' : ''}
            style={{ background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)', color: 'white' }}
          >
            <i className="fas fa-sign-out-alt"></i>
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main-content">
        {children || <Outlet />}
      </main>

      <style>{`
        .admin-layout {
          display: flex;
          min-height: 100vh;
          background: #f8f9fa;
        }

        .admin-sidebar {
          width: 260px;
          background: #ffffff !important;
          color: #333;
          display: flex;
          flex-direction: column;
          transition: width 0.3s ease;
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          z-index: 1000;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.08);
          border-right: 1px solid #e5e7eb;
        }

        .admin-sidebar.collapsed {
          width: 70px;
        }

        .sidebar-header {
          padding: 1.5rem 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #e5e7eb;
          gap: 0.5rem;
          background: #ffffff !important;
        }

        .brand {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          min-width: 0;
        }

        .brand i {
          font-size: 1.5rem;
          color: #b91c1c;
          flex-shrink: 0;
        }

        .brand-text {
          font-size: 0.95rem;
          font-weight: 700;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          word-break: break-word;
          color: #b91c1c;
        }

        .toggle-btn {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #b91c1c;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .toggle-btn:hover {
          background: #fee2e2;
          border-color: #b91c1c;
        }

        .sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding: 1rem 0;
          background: #ffffff !important;
        }

        .menu-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .menu-list li {
          margin: 0.25rem 0.75rem;
        }

        .menu-list li a {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          color: #4b5563;
          text-decoration: none;
          border-radius: 8px;
          transition: all 0.3s;
          background: transparent !important;
        }

        .menu-list li a:hover {
          background: #fef2f2 !important;
          color: #b91c1c;
        }

        .menu-list li.active a {
          background: #fef2f2 !important;
          color: #b91c1c;
          font-weight: 600;
          border-left: 3px solid #b91c1c;
        }

        .menu-list li a i {
          font-size: 1.25rem;
          width: 24px;
          text-align: center;
          color: inherit;
        }

        .menu-label {
          flex: 1;
          white-space: nowrap;
        }

        .sidebar-footer {
          padding: 1rem;
          border-top: 1px solid #e5e7eb;
          background: #ffffff !important;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: #f9fafb !important;
          border-radius: 8px;
          margin-bottom: 0.75rem;
          border: 1px solid #e5e7eb;
        }

        .user-info i {
          font-size: 1.5rem;
          color: #b91c1c;
        }

        .user-name {
          font-size: 0.875rem;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #374151;
        }

        .logout-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
          border: none;
          color: white;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s;
        }

        .logout-btn:hover {
          background: linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(185, 28, 28, 0.3);
        }

        .logout-btn i {
          font-size: 1.125rem;
        }

        .admin-main-content {
          flex: 1;
          margin-left: 260px;
          transition: margin-left 0.3s ease;
          min-height: 100vh;
          padding: 20px;
        }

        .admin-sidebar.collapsed + .admin-main-content {
          margin-left: 70px;
        }

        /* ==========================================
           CONSISTENT PAGE HEADERS - ALL PAGES
           ========================================== */

        /* Dashboard Header */
        .admin-main-content .dashboard-header,
        .admin-main-content .client-admin-dashboard .dashboard-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%) !important;
          padding: 20px !important;
          border-radius: 12px !important;
          margin-bottom: 20px !important;
          position: relative !important;
          overflow: hidden !important;
        }

        .admin-main-content .dashboard-header h1 {
          color: white !important;
          margin: 0 0 8px 0 !important;
          font-size: 1.75rem !important;
          font-weight: 700 !important;
        }

        .admin-main-content .dashboard-header .dashboard-subtitle {
          color: rgba(255,255,255,0.9) !important;
          margin: 0 !important;
        }

        /* Settings Page Header */
        .admin-main-content .settings-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%) !important;
          padding: 20px !important;
          border-radius: 12px !important;
          margin-bottom: 20px !important;
          position: relative !important;
          overflow: hidden !important;
        }

        .admin-main-content .settings-header .header-icon {
          background: rgba(255,255,255,0.2) !important;
          width: 60px !important;
          height: 60px !important;
          font-size: 1.75rem !important;
        }

        .admin-main-content .settings-header h1 {
          color: white !important;
          font-size: 1.75rem !important;
          font-weight: 700 !important;
          margin: 0 0 4px 0 !important;
        }

        .admin-main-content .settings-header p {
          color: rgba(255,255,255,0.9) !important;
          margin: 0 !important;
        }

        /* Generic Page Headers */
        .admin-main-content .page-header,
        .admin-main-content .management-header,
        .admin-main-content .page-header-banner,
        .admin-main-content .cms-header,
        .admin-main-content .editor-header,
        .admin-main-content .view-header,
        .admin-main-content .professional-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%) !important;
          padding: 20px !important;
          border-radius: 12px !important;
          margin-bottom: 20px !important;
          color: white !important;
          position: relative !important;
          overflow: hidden !important;
        }

        .admin-main-content .page-header h1,
        .admin-main-content .management-header h1,
        .admin-main-content .page-header-banner h1,
        .admin-main-content .page-header-banner .header-text h1,
        .admin-main-content .cms-header h1,
        .admin-main-content .editor-header h1,
        .admin-main-content .view-header h1,
        .admin-main-content .professional-header h1 {
          color: white !important;
          font-size: 1.75rem !important;
          font-weight: 700 !important;
          margin: 0 0 8px 0 !important;
        }

        .admin-main-content .page-header p,
        .admin-main-content .management-header p,
        .admin-main-content .page-header-banner p,
        .admin-main-content .page-header-banner .header-text p,
        .admin-main-content .cms-header p,
        .admin-main-content .editor-header p,
        .admin-main-content .view-header p,
        .admin-main-content .professional-header p {
          color: rgba(255,255,255,0.9) !important;
          margin: 0 !important;
        }

        .admin-main-content .page-header .header-icon,
        .admin-main-content .page-header-banner .header-icon,
        .admin-main-content .view-header .header-icon,
        .admin-main-content .page-header-container .header-icon {
          background: rgba(255,255,255,0.2) !important;
          color: white !important;
        }

        /* Page Header Container */
        .admin-main-content .page-header-container .page-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%) !important;
          padding: 20px !important;
          border-radius: 12px !important;
          color: white !important;
        }

        .admin-main-content .page-header-container .page-header h1 {
          color: white !important;
          font-size: 1.75rem !important;
          font-weight: 700 !important;
        }

        /* Section Titles */
        .admin-main-content .section-title,
        .admin-main-content .section-header h2 {
          color: #1e3a8a !important;
        }

        /* Settings Tab Active */
        .admin-main-content .tab-btn.active {
          background: linear-gradient(135deg, #1e40af, #2563eb) !important;
          color: white !important;
          border-color: #1e40af !important;
        }

        .admin-main-content .tab-btn:hover:not(.active) {
          background: #eff6ff !important;
          color: #1e3a8a !important;
        }

        @media (max-width: 768px) {
          .admin-sidebar {
            width: 70px;
          }

          .admin-sidebar .brand-text,
          .admin-sidebar .menu-label,
          .admin-sidebar .user-info,
          .admin-sidebar .logout-btn span {
            display: none;
          }

          .admin-sidebar .toggle-btn {
            display: none;
          }

          .admin-main-content {
            margin-left: 70px;
          }
        }
      `}</style>
    </div>
  )
}

export default ClientAdminLayout